// @ts-nocheck
import React, { useRef } from "react";
import { Textarea, Progress, ActionIcon } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Storage } from "aws-amplify";
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import { getCleanFileName,isEditableCustomerView } from "../../utils";
import { UploadIcon } from "../../assets/svgs";
import { CloseOutlined } from "@ant-design/icons";
import { BsCheckCircleFill } from "react-icons/bs";
import { IoIosDocument } from "react-icons/io";
import useOrder from "../../hooks/useOrder";
const ACCEPT_FILE_TYPES = [
  "application/pdf",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

type AccountUploadNotesProps = {
  furtherInfo: string;
  setFurtherInfo: (info: string) => void;
  files: any[];
  setFiles: (files: any[]) => void;
  furtherInfoFlowingMotifs: boolean;
  setFurtherInfoFlowingMotifs: (checked: boolean) => void;
  furtherInfoQualityCheck: boolean;
  setFurtherInfoQualityCheck: (checked: boolean) => void;
  order: UserOrder;
};

const AccountUploadNotes: React.FC<AccountUploadNotesProps> = ({
  furtherInfo,
  setFurtherInfo,
  files,
  setFiles,
  furtherInfoFlowingMotifs,
  setFurtherInfoFlowingMotifs,
  furtherInfoQualityCheck,
  setFurtherInfoQualityCheck,
  order,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<any>(null);
  const { handleOrderUpdate } = useOrder();

  const handleUploadNotes = async (selectedFiles: File[]) => {
    const newFiles: UploadFileProps[] = [];
    if (!order) return;
    const allowedExtensions = [
      "pdf",
      "csv",
      "xls",
      "xlsx",
      "xlsm",
      "xlsb",
      "xlt",
      "xla",
    ];
    for (let file of selectedFiles) {
      if (!file || !file.name) {
        console.warn("File or filename is undefined or null");
        continue;
      }
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        toast.warn(
          t("allowedFormats", { name: file.name, formats: "PDF, CSV, Excel" })
        );
        continue;
      }
      const sanitizedFileName = getCleanFileName(file.name);
      newFiles.push({
        file,
        key: `${order.id}/${Date.now()}-${sanitizedFileName}`,
        uploadStatus: "NOT_STARTED",
        progress: 0,
      });
    }
    setFiles([...files, ...newFiles]);
    for (let file of newFiles) {
      await startUpload(file);
    }
  };

  const startUpload = async (item: any) => {
    try {
      const uploadResponse = Storage.put(item.key, item.file, {
        contentType: item.file.type,
        resumable: true,
        progressCallback(progressObject: { loaded: number; total: number }) {
          const progress = Math.round(
            (progressObject.loaded / progressObject.total) * 100
          );
          setFiles(
            (prevFiles: any[]): any[] =>
              prevFiles.map((f) =>
                f.key === item.key ? { ...f, progress } : f
              ) as unknown[]
          );
        },
      });
      await uploadResponse;
      setFiles((prevFiles: any[]) =>
        prevFiles.map((f) =>
          f.key === item.key ? { ...f, uploadStatus: "DONE", progress: 100 } : f
        )
      );
      const currentUpdatedFile = { key: item.key, name: item.file.name };
      const _order = await handleOrderUpdate({
        id: order.id,
        files: [currentUpdatedFile],
      });
      toast.success(t("fileUploadedSuccessfully"));
    } catch (error) {
      toast.error(t("fileUploadFailed"));
      console.error("File upload error:", error);
    }
  };

  const handleRemove = (key: string) => {
    localStorage.removeItem("files");
    setFiles(files.filter((file) => file.key !== key));
    Storage.remove(key).catch((error) => {
      console.error("Error removing file from S3", error);
      toast.error(t("fileRemoveFailed"));
    });
  };

  const handleUploadText = async () => {
    if (furtherInfo.trim() === "") {
      toast.warn(t("noComments"));
      return;
    }

    const textFile = new Blob([furtherInfo], { type: "text/plain" });
    const fileName = `${Date.now()}-comments.txt`;

    try {
      await Storage.put(fileName, textFile, {
        contentType: "text/plain",
      });
      toast.success(t("textUploadedSuccessfully"));
    } catch (error) {
      toast.error(t("textUploadFailed"));
      console.error("Text upload error:", error);
    }
  };

  const handleUpload = (selectedFiles: FileWithPath[]) => {
    if (selectedFiles.length > 0) {
      handleUploadNotes(selectedFiles);
    } else {
      handleUploadText();
    }
  };

  return (
    <div className={`${
      !isEditableCustomerView(order.status) && 
      "opacity-disabled pointer-events-none"
    } p-6 bg-gray-100 rounded-lg shadow-md space-y-6`}>
      <div className="space-y-4">
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={furtherInfoFlowingMotifs}
            onChange={(e) =>
              setFurtherInfoFlowingMotifs(e.currentTarget.checked)
            }
            className="form-checkbox h-5 w-5 text-blue-600 rounded-none border-gray-300 focus:ring-blue-500"
          />
          <span className="text-gray-700">
            {t(
              "furtherInfoFlowingMotifs"
            )}
          </span>
        </label>
        {/* <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={furtherInfoQualityCheck}
            onChange={(e) =>
              setFurtherInfoQualityCheck(e.currentTarget.checked)
            }
            className="form-checkbox h-5 w-5 text-blue-600 rounded-none border-gray-300 focus:ring-blue-500"
          />
          <span className="text-gray-700">
            {t("furtherInfoQualityCheck")}
          </span>
        </label> */}
      </div>
      <Textarea
        label={t("notes")}
        placeholder={t("notes")}
        value={furtherInfo}
        onChange={(e) => setFurtherInfo(e.currentTarget.value)}
        minRows={4}
        className="w-full p-2 rounded mt-2 mb-6"
      />
      <p className="font-semibold mt-4 mb-2">
        {t("uploadOwnDeliveryNote")}
      </p>
      <div className="w-36">
        <Dropzone
          openRef={inputRef}
          accept={ACCEPT_FILE_TYPES}
          onDrop={handleUpload}
          maxFiles={1}
          multiple={false}
          className="w-auto cursor-pointer border-1 border-radius-4 p-0"
        >
          <div className="h-36 w-36 flex flex-col items-center justify-center">
            <div className="items-center justify-center">
              <UploadIcon />
            </div>
            <div className="items-center justify-center text-center">
              <div className="text-xs flex flex-col h-fit pt-2">
                <p
                  className="text-xs font-bold underline"
                  style={{ fontSize: "0.5rem", lineHeight: "0.75rem" }}
                >
                  {t("chooseFile")}
                </p>
                <p
                  className="text-xs font-bold"
                  style={{ fontSize: "0.5rem", lineHeight: "0.75rem" }}
                >
                  {t("orDrop")}
                </p>
                <p
                  className="text-xs"
                  style={{ fontSize: "0.5rem", lineHeight: "0.75rem" }}
                >
                  {t("deliveryNoteFileFormats")}
                </p>
              </div>
            </div>
          </div>
        </Dropzone>
      </div>
      <div>
        {files.map((item) => (
          <div
            key={item.key}
            className="card-auto p-16 mt-5 cursor-pointer"
            style={{
              border: item.uploadStatus === "DONE" ? "3px solid" : 0,
            }}
          >
            <div style={{ direction: "rtl" }}>
              <ActionIcon onClick={() => handleRemove(item.key)}>
                <CloseOutlined />
              </ActionIcon>
            </div>
            <div className="grid upload-file">
              <div>
                  <IoIosDocument size={"3rem"} className="mb-10" />
              </div>
              <div>
                <h4 className="overflow-wrap-break-word">
                  {getCleanFileName(item.key)}
                </h4>
                <span>
                  {item.file && item.file.size &&
                    `${(item.file.size / 1024 / 1024).toFixed(2)} MB`}
                </span>
                {item.file && item.file.size ?
                  <div className="grid row-v-center gap-16 align-progress-bar">
                    <div>
                      <Progress
                        value={item.progress ?? 0}
                        size={"sm"}
                        color={item.progress === 100 ? "green" : "black"}
                      />
                    </div>
                    <div>
                      <span>
                        {item.progress === 100 ? (
                          <BsCheckCircleFill color="green" />
                        ) : (
                          `${item.progress ?? 0}%`
                        )}
                      </span>
                    </div>
                  </div>
                  : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccountUploadNotes;
